import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  sendBuilderMessage,
  fetchSessions,
  createSession,
  fetchSession,
  deleteSession,
} from '../api';
import type { BuilderMessage, BuilderSession } from '../api';
import BuilderSidebar from '../components/builder/BuilderSidebar';
import WebsitePreview from '../components/builder/WebsitePreview';

const DEFAULT_SESSION_NAME = 'My Website';

const MainPage: React.FC = () => {
  const [messages, setMessages] = useState<BuilderMessage[]>([]);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState<BuilderSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentSessionName, setCurrentSessionName] = useState(DEFAULT_SESSION_NAME);
  const [publishToast, setPublishToast] = useState(false);

  const navigate = useNavigate();
  const { logout } = useAuth();

  const getToken = () => localStorage.getItem('authToken') || '';

  // Load sessions list on mount
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchSessions(getToken());
        setSessions(list);
      } catch {
        // ignore on startup
      }
    })();
  }, []);

  // Load a session: restore messages + html_content from server
  const loadSession = useCallback(async (sessionId: string) => {
    const token = getToken();
    try {
      const detail = await fetchSession(token, sessionId);
      setCurrentSessionId(detail.id);
      setCurrentSessionName(detail.name);
      setMessages(detail.messages);
      setHtmlContent(detail.html_content); // restore last generated HTML
    } catch {
      // ignore
    }
  }, []);

  const handleNewSession = useCallback(async () => {
    const token = getToken();
    try {
      const session = await createSession(token, DEFAULT_SESSION_NAME);
      setSessions(prev => [session, ...prev]);
      setCurrentSessionId(session.id);
      setCurrentSessionName(session.name);
      setMessages([]);
      setHtmlContent(null);
    } catch {
      // ignore
    }
  }, []);

  const handleSelectSession = useCallback(async (id: string) => {
    await loadSession(id);
  }, [loadSession]);

  const handleDeleteSession = useCallback(async (id: string) => {
    try {
      await deleteSession(getToken(), id);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (currentSessionId === id) {
        setCurrentSessionId(null);
        setCurrentSessionName(DEFAULT_SESSION_NAME);
        setMessages([]);
        setHtmlContent(null);
      }
    } catch {
      // ignore
    }
  }, [currentSessionId]);

  const handleSendMessage = useCallback(async (text: string) => {
    const token = getToken();
    let sessionId = currentSessionId;

    // Auto-create session on first message
    if (!sessionId) {
      try {
        const session = await createSession(token, DEFAULT_SESSION_NAME);
        setSessions(prev => [session, ...prev]);
        setCurrentSessionId(session.id);
        setCurrentSessionName(session.name);
        sessionId = session.id;
      } catch {
        return;
      }
    }

    // Optimistically add the user message to UI
    const tempUserMsg: BuilderMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      duration: 0,
    };
    setMessages(prev => [...prev, tempUserMsg]);
    setIsLoading(true);

    try {
      const result = await sendBuilderMessage(token, sessionId, text);

      // Replace temp user message with the persisted one from server, then add assistant
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMsg.id),
        result.userMessage,
        result.assistantMessage,
      ]);

      if (result.htmlContent) {
        setHtmlContent(result.htmlContent);
      }

      // Keep sessions list updated (updated_at changes)
      setSessions(prev =>
        prev.map(s => s.id === sessionId ? { ...s, updated_at: new Date().toISOString() } : s)
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      const errMsg: BuilderMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: msg,
        timestamp: new Date().toISOString(),
        duration: 0,
      };
      setMessages(prev => [...prev.filter(m => m.id !== tempUserMsg.id), tempUserMsg, errMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [currentSessionId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePublish = () => {
    setPublishToast(true);
    setTimeout(() => setPublishToast(false), 3000);
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      {/* ─── Top Header ─── */}
      <motion.header
        className="flex items-center justify-between h-12 px-4 border-b border-gray-100 bg-white flex-shrink-0"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Left: Logo + project name */}
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
          </div>
          <span className="font-semibold text-gray-800 text-sm">AI Website Builder</span>
          <span className="text-gray-300 text-sm select-none">/</span>
          <span className="text-gray-500 text-sm truncate max-w-[160px]">{currentSessionName}</span>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Logout
          </button>

          <button
            onClick={handlePublish}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Publish
          </button>
        </div>
      </motion.header>

      {/* ─── Main content ─── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar: Chat */}
        <motion.aside
          className="w-[300px] flex-shrink-0 flex flex-col h-full border-r border-gray-100"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <BuilderSidebar
            messages={messages}
            isLoading={isLoading}
            sessionName={currentSessionName}
            onSendMessage={handleSendMessage}
            onNewSession={handleNewSession}
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteSession}
          />
        </motion.aside>

        {/* Main: Website preview */}
        <motion.main
          className="flex-1 flex flex-col h-full overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <WebsitePreview html={htmlContent} isGenerating={isLoading} />
        </motion.main>
      </div>

      {/* Publish toast */}
      {publishToast && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl shadow-xl flex items-center gap-2 z-50"
        >
          <svg className="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Publishing feature coming soon!
        </motion.div>
      )}
    </div>
  );
};

export default MainPage;
